import { fromStorage, fromSession, toSession } from '@/etc/storage'
import { bitsArray } from '@/etc/utils'
import { LOG } from '@/etc/log'
const log = LOG.m('store::cmd')

const CHAINS = {
  axis: {
    KeyX: { help: 'axis X', action: 'setAxis', args: { axis: 'x' } },
    KeyY: { help: 'axis Y', action: 'setAxis', args: { axis: 'y' } },
    KeyZ: { help: 'axis Z', action: 'setAxis', args: { axis: 'z' } },
  },
  tools: {},
}
CHAINS.tools.KeyS = {
  help: 'Tool "Scale"',
  action: 'setTool',
  args: { tool: 'scale' },
  chain: CHAINS.axis,
}
CHAINS.tools.KeyR = {
  help: 'Tool "Rotate"',
  action: 'setTool',
  args: { tool: 'rotation' },
  chain: CHAINS.axis,
}
CHAINS.tools.KeyM = {
  help: 'Tool "Move"',
  action: 'setTool',
  args: { tool: 'position' },
  chain: CHAINS.axis,
}
CHAINS.tools.KeyG = {
  help: 'Tool "Move"',
  action: 'setTool',
  args: { tool: 'position' },
  chain: CHAINS.axis,
}

const HOTKEYS = {
  KeyZ_meta: { help: 'Undo', action: 'undo', args: {} },
  KeyZ_control: { help: 'Undo', action: 'undo', args: {} },
  KeyZ_meta_shift: { help: 'Redo', action: 'redo', args: {} },
  KeyZ_control_shift: { help: 'Redo', action: 'redo', args: {} },
  Space: { help: 'play/stop', action: 'playStop', args: {} },
  KeyP: { help: 'play/pause', action: 'playPause', args: {} },
  ...CHAINS.tools,
  KeyF: { help: 'Toggle Fullscreen', action: 'toggleFullscreen', args: {} },
  KeyC: {
    help: 'Toggle panel "Clips"',
    action: 'togglePanel',
    args: { panel: 'library' },
  },
  KeyE: {
    help: 'Toggle panel "Effects"',
    action: 'togglePanel',
    args: { panel: 'effects' },
  },
  KeyA: {
    help: 'Toggle panel "AI"',
    action: 'togglePanel',
    args: { panel: 'ai' },
  },
  KeyD: {
    help: 'Toggle panel "Props"',
    action: 'togglePanel',
    args: { panel: 'props' },
  },
  KeyK: {
    help: 'Toggle panel "Animation"',
    action: 'togglePanel',
    args: { panel: 'keyframes' },
  },
  KeyV_shift: {
    help: 'Toggle panel "Vyby"',
    action: 'togglePanel',
    args: { panel: 'vyby' },
  },
  KeyV: {
    help: 'Talk to "Vyby"',
    down: { action: 'talkToVyby', args: {} },
    up: { action: 'endTalkToVyby', args: {} },
  },
  ArrowRight: {
    help: 'Jump beat forward',
    action: 'jumpByBeats',
    args: { direction: 'Forward' },
  },
  ArrowLeft: {
    help: 'Jump beat backward',
    action: 'jumpByBeats',
    args: { direction: 'Backward' },
  },
  ArrowUp: {
    help: 'Jump clip forward',
    action: 'jumpByClips',
    args: { direction: 'Forward' },
  },
  ArrowUp_shift: {
    help: 'Jump clip Backward',
    action: 'jumpByClips',
    args: { direction: 'Backward' },
  },
  ArrowDown: {
    help: 'Drop Keyframe',
    action: 'dropKeyframe',
    args: {},
    chain: CHAINS.tools,
  },
  Backspace: {
    help: 'Delete selected clip',
    action: 'dropCurrentClip',
    args: {},
  },
  Delete: { help: 'Delete selected clip', action: 'dropCurrentClip', args: {} },
  Slash_shift: {
    help: 'Show hotkeys help',
    up: { action: 'closePanel', args: { panel: 'hotkeysHelp' } },
    down: { action: 'openPanel', args: { panel: 'hotkeysHelp' } },
  },
  // Export In/Out (CapCut / Resolve style). I marks where the export starts,
  // O marks where it ends, both default to the playhead's current time.
  // Shift+X clears the range and returns to "export full timeline" (auto).
  KeyI: { help: 'Set export In point', action: 'setExportInAtPlayhead', args: {} },
  KeyO: { help: 'Set export Out point', action: 'setExportOutAtPlayhead', args: {} },
  KeyX_shift: { help: 'Clear export In/Out', action: 'clearExportInOut', args: {} },
}

const HOTKEYS_USER = fromStorage('ui/hotkeys', {})

export const HOTKEYS_LIST = Object.keys(HOTKEYS).map((default_key) => ({
  default_key,
  key: default_key,
  ...HOTKEYS[default_key],
  ...(HOTKEYS_USER[default_key] || {}),
}))

const hotkeyCmd = (e, dir, chain = []) => {
  // macos modifiers: Command = meta, Option = alt
  const modifiers = {
    shift: e.shiftKey,
    control: e.ctrlKey,
    alt: e.altKey,
    meta: e.metaKey,
  }
  const mlist = Object.keys(modifiers).filter((m) => modifiers[m])
  let source = HOTKEYS_LIST
  if (Array.isArray(chain) && chain.length) {
    const last = chain[chain.length - 1]
    if (last.chain) {
      source = Object.keys(last.chain).map((default_key) => ({
        default_key,
        key: default_key,
        ...last.chain[default_key],
        ...(HOTKEYS_USER[default_key] || {}),
      }))
    }
  }
  return source.find((hk) => {
    const [key, ...mods] = hk.key.split('_')
    if (key !== e.code) return false
    if (mlist.length && !mods.length) return false
    if (mods.length) {
      if (mods.some((m) => !modifiers[m])) return false
    }
    if (dir === 'down') {
      if (hk.up) {
        return !!hk.down
      }
    }
    if (dir === 'up') {
      return !!hk.up
    }
    return true
  })
}

const sess = fromSession('ui/panels', {
  library: { open: false, group: ['left'] },
  effects: { open: false, group: ['left'] },
  ai: { open: false, group: ['left'] },
  favorites: { open: false, group: ['left'] },
  text: { open: false, group: ['left'] },
  props: { open: false, group: ['right'] },
  settings: { open: false, group: ['right'] },
  export: { open: false, group: ['right'] },
  aiTool: { open: false, group: ['right', 'screen'] },
  keyframes: { open: false, group: [] },
  hotkeysHelp: { open: false, group: [] },
  vyby: { open: false, group: [] },
})

const getMicrophone = async () => {
  /*
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return new MediaRecorder(stream);
  } catch (error) {
    console.error("Error accessing microphone:", error);
  }
  */
  return null
}

export default {
  namespaced: true,
  state: {
    panels: {
      library: { open: !!sess.library.open, group: ['left'] },
      effects: { open: !!sess.effects.open, group: ['left'] },
      ai: { open: !!sess.ai.open, group: ['left'] },
      favorites: { open: !!sess.favorites.open, group: ['left'] },
      text: { open: !!sess.text.open, group: ['left'] },
      props: { open: !!sess.props.open, group: ['right'] },
      settings: { open: !!sess.settings.open, group: ['right'] },
      export: { open: !!(sess.export || {}).open, group: ['right'] },
      aiTool: { open: !!(sess.aiTool || {}).open, group: ['right'] },
      keyframes: { open: !!sess.keyframes.open, group: [] },
      hotkeysHelp: { open: !!sess.hotkeysHelp.open, group: [] },
      vyby: { open: !!sess.vyby.open, group: [] },
    },
    playStopTime: 0,
    chain: null,
    chainTimout: null,
    mic: null,
    micRecording: false,
  },
  mutations: {
    mic: (state, next) => {
      state.mic = next
    },
    micRecording: (state, next) => {
      state.micRecording = next
    },
    setPanel: (state, { panel, next }) => {
      log.info('set panel', { panel, next, state: state.panels[panel] })
      state.panels[panel].group.forEach((g) => {
        Object.keys(state.panels).forEach((k) => {
          if (state.panels[k].group.includes(g)) state.panels[k].open = false
        })
      })
      state.panels[panel].open = next
      toSession('ui/panels', state.panels)
    },
    playStopTime: (state, next) => {
      state.playStopTime = next
    },
    toChain: (state, next) => {
      if (!state.chain) state.chain = []
      state.chain.push(next)
      if (state.chainTimout) clearTimeout(state.chainTimout)
      log.info('2chain', state.chain)
      state.chainTimout = setTimeout(() => {
        state.chain = null
        log.info('clear chain')
      }, 2500)
    },
  },
  actions: {
    undo: async (store) => {
      const hist = store.rootState.project._history
      if (!hist || !hist.undo.length) return
      const snap = hist.undo.pop()
      // Push current as redo
      const cur = {
        tracks: JSON.parse(JSON.stringify(store.rootState.project.tracks)),
        timeline: JSON.parse(JSON.stringify(store.rootState.project.timeline)),
        settings: JSON.parse(JSON.stringify(store.rootState.project.settings)),
      }
      hist.redo.push(cur)
      store.commit('project/_restoreSnapshot', snap, { root: true })
    },
    redo: async (store) => {
      const hist = store.rootState.project._history
      if (!hist || !hist.redo.length) return
      const snap = hist.redo.pop()
      // Push current as undo
      const cur = {
        tracks: JSON.parse(JSON.stringify(store.rootState.project.tracks)),
        timeline: JSON.parse(JSON.stringify(store.rootState.project.timeline)),
        settings: JSON.parse(JSON.stringify(store.rootState.project.settings)),
      }
      hist.undo.push(cur)
      store.commit('project/_restoreSnapshot', snap, { root: true })
    },
    hotkey: async (store, e) => {
      const cmd = hotkeyCmd(e, 'down', store.state.chain)
      log.debug('hotkey', e, { cmd })
      if (!cmd) return
      if (cmd.chain) store.commit('toChain', cmd)
      const action = cmd.down || cmd
      store.dispatch(action.action, action.args)
    },
    hotkeyUp: async (store, e) => {
      const cmd = hotkeyCmd(e, 'up', store.state.chain)
      if (!cmd) return
      // log.info('hotkeyUp', { cmd })
      const action = cmd.up || cmd
      store.dispatch(action.action, action.args)
    },
    togglePanel: async (store, { panel }) => {
      log.info('togglePanel', panel)
      // if (panel === 'vyby')
      store.commit('setPanel', { panel, next: !store.state.panels[panel].open })
    },
    toggleSidePanels: async (
      store,
      { leftPanel = 'library', rightPanel = 'props' } = {}
    ) => {
      const panels = store.state.panels
      const leftOpen = Object.keys(panels).some(
        (k) => panels[k].open && panels[k].group.includes('left')
      )
      const rightOpen = Object.keys(panels).some(
        (k) => panels[k].open && panels[k].group.includes('right')
      )

      // If either side is open, collapse both groups.
      if (leftOpen || rightOpen) {
        store.commit('setPanel', { panel: leftPanel, next: false })
        store.commit('setPanel', { panel: rightPanel, next: false })
        return
      }

      // Otherwise, open defaults (or provided targets) on both sides.
      await store.dispatch('openPanel', { panel: leftPanel })
      await store.dispatch('openPanel', { panel: rightPanel })
    },
    openPanel: async (store, { panel }) => {
      // log.info('openPanel', panel)
      store.commit('setPanel', { panel, next: true })
      // Text workflow: bind marker placement to the camera strip, while remembering the last
      // active layer track as the insertion target for created text strips.
      if (panel === 'text') {
        try {
          const tuuid = store.rootState.project?.timeline?.activeTrack
          const track = tuuid ? store.rootState.project?.tracks?.[tuuid] : null
          if (track && track.type === 'layer') {
            toSession('ui/text/insertTrack', tuuid)
          }
        } catch (e) { /* noop */ }
        try {
          const cam = store.rootState.project?.cameraStrip
          if (cam) store.dispatch('project/activeStrip', cam, { root: true })
        } catch (e) { /* noop */ }
      }
    },
    closePanel: async (store, { panel }) => {
      // log.info('closePanel', panel)
      store.commit('setPanel', { panel, next: false })
    },
    openMic: (store) =>
      new Promise((resolve) => {
        getMicrophone()
          .then((mic) => {
            store.commit('mic', mic)
            mic.onstart = () => {
              store.commit('micRecording', true)
              resolve()
            }
            mic.onstop = () => {
              store.commit('micRecording', false)
            }
            mic.ondataavailable = (e) => {
              console.warn('micData', e)
              if (e.data.size > 0) {
                // e.data.arrayBuffer().then(buff => {
                store.rootState.io._io.core.emit('vyby:mic:data', e.data)
                // })
              }
            }
            mic.start(1000)
          })
          .catch((err) => {
            console.error('getMic error', err)
          })
      }),
    closeMic: async (store) => {
      if (!store.state.mic) return
      await store.state.mic.stop()
      store.commit('mic', null)
    },
    talkToVyby: async (store) => {
      if (store.state.micRecording) return
      await store.dispatch('openPanel', { panel: 'vyby' })
      await store.dispatch('openMic')
    },
    endTalkToVyby: async (store) => {
      // store.commit('openPanel', { panel: 'vyby' })
      store.dispatch('closeMic')
    },
    jumpByClips: async (store, { direction }) => {
      // log.info('jumpByClips', direction)
      const track = store.rootGetters['project/activeTrack']
      if (!track) return
      const strips = Object.values(track.strips)
      if (!strips.length) return
      const time = store.rootState.project.timeline.currentTime
      const times = []
      strips.forEach((strip) => {
        times.push(strip.start)
        times.push(strip.start + strip.length)
      })
      const bit = times.findIndex((t) => t === time)
      const bitb2 = times.findIndex((t) => t > time)
      const bitb4 = bitb2 >= 0 ? bitb2 - (bit >= 0 ? 2 : 1) : times.length - 2
      let next = time
      if (direction === 'Backward' && bitb4 >= 0) next = times[bitb4]
      if (direction === 'Forward' && bitb2 < times.length) next = times[bitb2]
      if (typeof next !== 'number' || next === time) return
      // log.info('jumpByBeats', direction, times, next)
      store.commit('project/currentTime', next, { root: true })
    },
    jumpByBeats: async (store, { direction }) => {
      const time = store.rootState.project.timeline.currentTime
      const snapOn = !!store.rootState.project.snap2beat
      const unit = store.rootState.project.snapUnit || 'quarter'

      // When snapping (magnet) is enabled, jump by the selected snap unit instead of
      // the legacy quarter-note grid.
      if (snapOn) {
        const fps = Number(store.rootState.project.settings?.fps) || 24
        const bc = store.rootGetters['project/beatCode']
        const step =
          unit === 'frame'
            ? 1 / fps
            : unit === 'quarter'
            ? bc.secondsPerQuarter()
            : unit === 'eighth'
            ? bc.secondsFor('eighth')
            : unit === 'half'
            ? bc.secondsFor('half')
            : unit === 'whole'
            ? bc.secondsFor('whole')
            : unit === 'sixteenth'
            ? bc.secondsFor('sixteenth')
            : bc.secondsPerQuarter()

        const eps = 1e-9
        const q = step > 0 ? time / step : 0
        const onGrid = Math.abs(q - Math.round(q)) < eps

        let next = time
        if (direction === 'Forward') {
          next = (onGrid ? Math.round(q) + 1 : Math.ceil(q)) * step
        } else {
          next = (onGrid ? Math.round(q) - 1 : Math.floor(q)) * step
        }

        if (typeof next === 'number' && Number.isFinite(next) && next !== time) {
          store.commit('project/currentTime', Math.max(0, next), { root: true })
        }
        return
      }

      const bits = bitsArray(
        store.rootState.project,
        Math.max(0, time - 3000),
        time + 3000
      )
      const bit = bits.findIndex((b) => b.time === time)
      const bitb2 = bits.findIndex((b) => b.time > time)
      const bitb4 = bitb2 - (bit >= 0 ? 2 : 1)
      let next = time
      if (direction === 'Backward' && bitb4 >= 0) next = bits[bitb4].time
      if (direction === 'Forward' && bitb2 < bits.length)
        next = bits[bitb2].time
      if (next === time) return
      // log.info('jumpByBeats', direction, bits, next)
      store.commit('project/currentTime', next, { root: true })
    },
    dropKeyframe: async (store, x) => {
      log.info('dropKeyframe', x)
      const time = store.rootState.project.timeline.currentTime
      store.commit('project/addEmptyKeyframe', time, { root: true })
    },
    playPause: async (store) => {
      const isPlaying = store.rootState.project.playing
      if (isPlaying) store.dispatch('project/pause', null, { root: true })
      else store.dispatch('project/play', null, { root: true })
    },
    playStop: async (store) => {
      const isPlaying = store.rootState.project.playing
      if (!isPlaying) {
        store.commit(
          'playStopTime',
          store.rootState.project.timeline.currentTime
        )
        store.dispatch('project/play', null, { root: true })
      } else {
        store.dispatch('project/pause', null, { root: true })
      }
      // log.info('playStop', isPlaying)
    },
    toggleFullscreen: async (store) => {
      store.commit('project/fullScreen', !store.rootState.project.fullScreen, {
        root: true,
      })
    },
    dropCurrentClip: async (store, withLib) => {
      log.info('dropCurrentClip', withLib)
      // If the Keyframes panel is open and a keyframe is selected, treat Delete/Backspace as "delete keyframe"
      // (never delete the whole strip by accident).
      try {
        const keyframesOpen = !!store.state.panels?.keyframes?.open
        const activeKfUuid = store.rootState?.project?.timeline?.activeKeyframe
        if (keyframesOpen && activeKfUuid) {
          store.commit('project/removeSelectedKeyframeSelection', null, { root: true })
          return
        }
      } catch (e) { /* noop */ }

      const activeTrack = store.rootGetters['project/activeTrack']
      const activeClip = store.rootGetters['project/activeStrip']
      if (!activeTrack || !activeClip) return
      // Never delete the camera strip via hotkey (camera is required for the editor).
      try {
        const cameraStrip = store.rootState?.project?.cameraStrip
        if (cameraStrip && activeClip.uuid === cameraStrip) return
        if (activeClip?.asset?.type === 'camera') return
      } catch (e) { /* noop */ }
      store.dispatch(
        'project/removeStrip',
        { strip: activeClip.uuid, track: activeTrack.uuid },
        { root: true }
      )
    },
    setTool: async (store, { tool }) => {
      // log.info('setTool', tool)
      const activeClip = store.rootGetters['project/activeStrip']
      if (!activeClip) return
      store.dispatch('project/toggleTool', tool, { root: true })
    },
    setAxis: async (store, { axis }) => {
      const activeClip = store.rootGetters['project/activeStrip']
      if (!activeClip) return
      log.info('setAxis', axis)
    },
    trimAtPosition: async (store, x) => {
      log.info('trimAtPosition', x)
    },
    exec: async (store, text) => {
      log.info('exec command', text)
    },
    // Export In/Out hotkey actions. Reuse the existing setExportIn /
    // setExportOut / setExportAuto mutations on project/timeline so the
    // new hotkeys stay 100% consistent with any panel UI that already
    // touches the export range.
    setExportInAtPlayhead: async (store) => {
      const t = Number(store.rootState.project?.timeline?.currentTime)
      if (!Number.isFinite(t)) return
      store.commit('project/setExportIn', t, { root: true })
    },
    setExportOutAtPlayhead: async (store) => {
      const t = Number(store.rootState.project?.timeline?.currentTime)
      if (!Number.isFinite(t)) return
      store.commit('project/setExportOut', t, { root: true })
    },
    clearExportInOut: async (store) => {
      store.commit('project/setExportAuto', true, { root: true })
    },
  },
}
